import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { History, RotateCcw } from 'lucide-react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';

import { Alert } from '../../components/ui/Alert';
import { askDataQa } from '../../services/api/dataQa';
import { useAuth } from '../../utils/authcontextt';
import { KvalitetSessionLink } from '../sessions/KvalitetSessionLink';

import { buildHistory } from './history';
import { QueryDetails } from './QueryDetails';
import { ResultChart } from './ResultChart';
import { sessionIdFromHref, sessionIdLinks } from './session-id-links';
import { ThreadHistory } from './ThreadHistory';
import { appendEntries, deleteThread, openThread, startThread, useThreads } from './thread-store';

const EXAMPLE_QUESTIONS = [
  'Hur många returfrågor har vi fått denna månad?',
  'Hur många gånger har kund 12345 chattat, och om vad?',
  'Har kunden med ordernummer 123456 chattat, och om vad?',
  'Hur fördelar sig chattarna per frågetyp de senaste fyra veckorna?',
  'Hur många chattar gick till kundservice förra veckan, och varför?',
  'Hur många inloggade kunder chattade förra veckan, jämfört med gäster?',
  'Vilka frågetyper får sämst betyg av kunderna?',
  'Löste boten ärendena förra veckan?',
  'Jämför returfrågorna denna månad med förra månaden',
  'Ge mig en veckorapport per frågetyp och marknad',
  'Vad gör boten med en fråga, i korta ordalag?',
];

const describeError = (err: unknown): string => {
  if (axios.isAxiosError(err)) {
    if (err.response?.status === 403) return 'Du har inte behörighet till Data Q/A.';
    if (err.response?.status === 400)
      return 'Frågan kunde inte skickas. Kontrollera texten och försök igen.';
  }
  return 'Något gick fel när frågan ställdes. Försök igen om en stund.';
};

const markdownClasses =
  'text-sm leading-relaxed text-black/90 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_strong]:font-semibold [&_table]:my-2 [&_table]:text-xs [&_th]:border-b [&_th]:border-black/10 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:px-2 [&_td]:py-1 [&_code]:rounded-sm [&_code]:bg-brand-beige-tint-2 [&_code]:px-1';

const remarkPlugins = [remarkGfm, sessionIdLinks];

const markdownComponents: Components = {
  a: ({ href, children, node: _node, ...props }) => {
    const sessionId = sessionIdFromHref(href);
    return sessionId ? (
      <KvalitetSessionLink sessionId={sessionId} newTab />
    ) : (
      <a {...props} href={href} target="_blank" rel="noopener noreferrer" className="underline">
        {children}
      </a>
    );
  },
};

const headerButtonClasses =
  'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-sm border border-black/10 bg-white px-3 py-2 text-xs uppercase tracking-wider text-brand-blue transition-colors hover:border-brand-blue';

export const DataQaPage = () => {
  const { user } = useAuth();
  const userId = user?.id ?? 'okänd';
  const { activeId, threads } = useThreads(userId);
  const entries = threads.find((thread) => thread.id === activeId)?.entries ?? [];

  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [entries.length, loading]);

  const send = async (question: string) => {
    const content = question.trim();
    if (!content || loading) return;
    const history = buildHistory(entries, content);
    // Tråd-id:t låses här: svaret ska hamna i den tråd frågan ställdes i, även om
    // användaren hinner öppna en annan tråd medan vi väntar.
    const threadId = appendEntries(userId, activeId, [{ role: 'user', content }]);
    setDraft('');
    setError(null);
    setLoading(true);
    try {
      const answer = await askDataQa(history);
      appendEntries(userId, threadId, [
        {
          role: 'assistant',
          content: answer.answer,
          queries: answer.queries,
          charts: answer.charts,
          model: answer.model,
        },
      ]);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
      textareaRef.current?.focus();
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void send(draft);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void send(draft);
    }
  };

  const reset = () => {
    startThread(userId);
    setError(null);
    setDraft('');
    setHistoryOpen(false);
    textareaRef.current?.focus();
  };

  const open = (threadId: string) => {
    openThread(userId, threadId);
    setError(null);
    setHistoryOpen(false);
    textareaRef.current?.focus();
  };

  return (
    <section className="flex h-[calc(100vh-3rem)] flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold text-brand-blue">Data Q/A</h1>
          <p className="mt-1 max-w-2xl text-sm text-text-grey">
            Fråga om chattarna i klartext. Under varje svar ser du hur jag räknade.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {threads.length > 0 ? (
            <button
              type="button"
              onClick={() => setHistoryOpen((value) => !value)}
              aria-expanded={historyOpen}
              className={headerButtonClasses}
            >
              <History className="h-3.5 w-3.5" />
              Tidigare frågor
            </button>
          ) : null}
          {entries.length > 0 ? (
            <button type="button" onClick={reset} className={headerButtonClasses}>
              <RotateCcw className="h-3.5 w-3.5" />
              Ny fråga
            </button>
          ) : null}
        </div>
      </header>

      {historyOpen ? (
        <ThreadHistory
          threads={threads}
          activeId={activeId}
          onOpen={open}
          onDelete={(threadId) => deleteThread(userId, threadId)}
        />
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-black/10 bg-white shadow-sm">
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
          {entries.length === 0 ? (
            <div className="flex justify-start">
              <div className="min-w-0 max-w-[85%] space-y-3 rounded-2xl rounded-bl-sm border border-black/10 bg-brand-beige-tint-2/40 px-4 py-3">
                <p className="text-sm leading-relaxed text-black/90">
                  Hej! Jag svarar på frågor om chattarna: hur många som chattat och från vilka
                  marknader, hur ofta det blir kundservice och varför, vad kunderna frågar om, hur
                  en viss kund har chattat (via kundnummer, ordernummer, e-post eller namn), hur
                  många som var inloggade, och vad domaren och kundernas tummar säger.
                </p>
                <p className="text-sm leading-relaxed text-black/90">
                  Gästchattar hittar jag via ordernummer eller e-post, men jag visar aldrig namn,
                  adresser eller ordernummer i svaret. Jag ser inte själva ordrarna, leveranserna
                  eller lagret, och jag kan inte ändra något. Prova till exempel:
                </p>
                <div className="flex flex-wrap gap-2">
                  {EXAMPLE_QUESTIONS.map((question) => (
                    <button
                      key={question}
                      type="button"
                      onClick={() => {
                        setDraft(question);
                        textareaRef.current?.focus();
                      }}
                      className="rounded-full border border-brand-blue/30 bg-white px-3 py-1.5 text-left text-xs text-brand-blue transition-colors hover:border-brand-blue hover:bg-brand-beige-tint-2"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {entries.map((entry, index) =>
            entry.role === 'user' ? (
              <div key={index} className="flex justify-end">
                <div className="max-w-[75%] rounded-2xl rounded-br-sm bg-brand-blue px-4 py-2.5 text-sm text-white">
                  {entry.content}
                </div>
              </div>
            ) : (
              <div key={index} className="flex justify-start">
                <div className="min-w-0 max-w-[85%] space-y-3 rounded-2xl rounded-bl-sm border border-black/10 bg-brand-beige-tint-2/40 px-4 py-3">
                  <div className={markdownClasses}>
                    <ReactMarkdown remarkPlugins={remarkPlugins} components={markdownComponents}>
                      {entry.content}
                    </ReactMarkdown>
                  </div>
                  {entry.charts?.map((chart, chartIndex) => {
                    const query = entry.queries?.[chart.queryIndex];
                    return query && query.rows.length > 0 ? (
                      <ResultChart key={chartIndex} spec={chart} rows={query.rows} />
                    ) : null;
                  })}
                  {entry.queries && entry.queries.length > 0 ? (
                    <div className="space-y-2">
                      {entry.queries.map((query, queryIndex) => (
                        <QueryDetails key={queryIndex} query={query} index={queryIndex} />
                      ))}
                    </div>
                  ) : null}
                  {entry.model ? (
                    <div className="text-[10px] text-text-grey">Modell: {entry.model}</div>
                  ) : null}
                </div>
              </div>
            ),
          )}

          {loading ? (
            <div className="flex items-center gap-2 text-xs text-text-grey">
              <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-brand-blue/30 border-t-brand-blue" />
              Räknar…
            </div>
          ) : null}

          {error ? <Alert type="error" message={error} /> : null}
          <div ref={bottomRef} />
        </div>

        <form onSubmit={handleSubmit} className="border-t border-black/10 bg-white px-5 py-4">
          <div className="flex items-end gap-3">
            <textarea
              ref={textareaRef}
              rows={2}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Skriv din fråga… (Enter skickar, Shift+Enter radbryter)"
              disabled={loading}
              aria-label="Fråga om datan"
              className="flex-1 resize-none overflow-y-auto rounded-sm border border-black/10 bg-white px-4 py-3 text-base text-brand-blue placeholder:text-text-grey transition-colors focus:border-brand-blue focus:outline-none disabled:cursor-not-allowed disabled:bg-brand-beige-tint-2 disabled:opacity-70"
            />
            <button
              type="submit"
              disabled={loading || draft.trim().length === 0}
              className="min-w-[110px] rounded-sm border border-brand-blue bg-brand-blue px-5 py-3 text-xs uppercase tracking-wider text-white transition-colors duration-200 hover:bg-white hover:text-brand-blue disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <div className="mx-auto h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              ) : (
                'Skicka'
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};
